import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { UserMinus } from "lucide-react";
import { Search } from "lucide-react";
import { MemberRole } from '@iziping/common';
import { QUERY_CACHE } from '@/model/endpoints';
import {
  useGetTournamentMembers,
  useInviteTournamentMember,
  useRemoveTournamentMember,
} from "../hooks/tournamentHooks";
import { useNotification } from '@iziping/ui';
import { useAuthStore } from '@iziping/common';
import { useTournamentStore } from '../../../service/useTournamentStore';
import ConfirmationModal from "../../../components/shared/ConfirmationModal";
import { InputIcon } from "@iziping/ui";

const TournamentMembers: React.FC = () => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const { selectedTournament } = useTournamentStore();
  const tournamentId = Number(selectedTournament?.id);
  const queryClient = useQueryClient();
  const currentUserId = useAuthStore((s) => s.user?.id);

  const [login, setLogin] = useState("");
  const [confirmRevokeId, setConfirmRevokeId] = useState<number | null>(null);

  const { data: members, isLoading } = useGetTournamentMembers(tournamentId);
  const invite = useInviteTournamentMember(tournamentId);
  const remove = useRemoveTournamentMember(tournamentId);

  const isOwner = members?.some(
    (m) => m.userId === currentUserId && m.role === MemberRole.OWNER,
  );

  const handleInvite = async () => {
    const trimmed = login.trim();
    if (!trimmed) return;
    try {
      await invite.mutateAsync({ login: trimmed });
      await queryClient.invalidateQueries({
        queryKey: [QUERY_CACHE.TOURNAMENT_MEMBERS, tournamentId],
      });
      setLogin("");
      notify(t("tournament.members.invite.success"), "success");
    } catch (err: any) {
      notify(t(err?.error) || t("internal.server.error"), "error");
    }
  };

  const handleRevoke = async () => {
    if (!confirmRevokeId) return;
    try {
      await remove.mutateAsync({ userId: confirmRevokeId });
      await queryClient.invalidateQueries({
        queryKey: [QUERY_CACHE.TOURNAMENT_MEMBERS, tournamentId],
      });
      setConfirmRevokeId(null);
      notify(t("tournament.members.revoke.success"), "success");
    } catch (err: any) {
      setConfirmRevokeId(null);
      notify(t(err?.error) || t("internal.server.error"), "error");
    }
  };

  if (isLoading) return <div>Loading...</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <ConfirmationModal
        isOpen={confirmRevokeId !== null}
        message={t("tournament.members.revoke.confirm")}
        onConfirm={handleRevoke}
        onCancel={() => setConfirmRevokeId(null)}
      />

      {isOwner && (
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <InputIcon
            icon={Search}
            placeholder={t("tournament.members.invite.placeholder") as string}
            value={login}
            onChange={(e) => setLogin(e.target.value)}
            onKeyUp={(value) => setLogin(value)}
          />
          <button
            className="button"
            onClick={handleInvite}
            disabled={invite.isPending || !login.trim()}
            style={{ flexShrink: 0 }}
          >
            {t("tournament.members.invite.button")}
          </button>
        </div>
      )}

      <table className="table-stripped">
        <thead>
          <tr>
            <th>{t("tournament.members.firstname")}</th>
            <th>{t("tournament.members.lastname")}</th>
            <th>{t("tournament.members.login")}</th>
            <th>{t("tournament.members.role")}</th>
            {isOwner && <th></th>}
          </tr>
        </thead>
        <tbody>
          {members?.map((member) => (
            <tr key={member.userId}>
              <td>{member.firstname}</td>
              <td>{member.lastname}</td>
              <td>{member.login}</td>
              <td>{t(`tournament.members.role.${member.role}`)}</td>
              {isOwner && (
                <td>
                  {member.role !== MemberRole.OWNER && (
                    <button onClick={() => setConfirmRevokeId(member.userId)}>
                      <UserMinus fill="currentColor" />
                    </button>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default TournamentMembers;
